<script lang="ts">
  // The top search's Skills rows: the workspace's own skills first, then marketplace hits the
  // workspace does not hold. Rows only, like PageSearchResults: the section label, "Show N
  // more" and the failure line belong to the caller. Rows carry data-index for the arrows.
  import { cn } from "$lib/utils.js";
  import { highlightParts } from "$lib/search/global-search.js";
  import SkillIconTile from "./SkillIconTile.svelte";
  import { iconForCategory } from "./skill-icons.js";
  import { humanizeSkillName } from "./skill-upload.js";
  import { MARKETPLACE_LABELS } from "./skills-marketplace-view-model.js";
  import type { SkillSearchHit } from "./skills-library-view-model.js";

  let {
    results,
    query,
    startIndex = 0,
    activeIndex = -1,
    onhover,
    onselect,
  }: {
    results: SkillSearchHit[];
    query: string;
    startIndex?: number;
    activeIndex?: number;
    onhover?: (index: number) => void;
    onselect: (hit: SkillSearchHit) => void;
  } = $props();

  function keyOf(hit: SkillSearchHit): string {
    return hit.kind === "workspace" ? `w:${hit.skill.id}` : `m:${hit.result.id}`;
  }

  function subline(hit: SkillSearchHit): string {
    if (hit.kind === "marketplace") {
      return [MARKETPLACE_LABELS[hit.result.source], hit.result.description].filter(Boolean).join(" · ");
    }
    const n = hit.skill.assignments.length;
    return `Workspace skill · ${n === 0 ? "no agents" : `${n} ${n === 1 ? "agent" : "agents"}`}`;
  }
</script>

{#snippet marked(text: string)}
  {@const parts = highlightParts(text, query)}
  {parts[0]}{#if parts[1]}<mark class="rounded-sm bg-warning/30 px-0.5 text-content">{parts[1]}</mark>{/if}{parts[2]}
{/snippet}

{#each results as hit, i (keyOf(hit))}
  {@const idx = startIndex + i}
  <button
    type="button"
    data-index={idx}
    data-result-kind="skill"
    onclick={() => onselect(hit)}
    onmousemove={() => onhover?.(idx)}
    class={cn(
      "flex w-full cursor-pointer items-center gap-2.5 border-b border-edge px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-raised",
      activeIndex === idx && "bg-raised",
    )}
  >
    <SkillIconTile icon={hit.kind === "workspace" ? hit.skill.icon : iconForCategory(hit.result.category)} />
    <span class="flex min-w-0 flex-1 flex-col">
      <span class="truncate text-sm font-semibold text-content">{@render marked(humanizeSkillName(hit.kind === "workspace" ? hit.skill.name || hit.skill.slug : hit.result.name))}</span>
      <span class="truncate text-xs text-content-dim">{@render marked(subline(hit))}</span>
    </span>
  </button>
{/each}
